import supabase from '../config/supabase';
import config from '../config/environment';
import { handleServiceError } from '../utils/errorHandler';
import type { PaginationInfo } from '../types';
import type { StudentConsolidatedHistoryResponse } from '../types/studentConsolidatedHistory';

export type StudentHistorySection =
  | 'workouts'
  | 'fups'
  | 'fup-attempts'
  | 'notes'
  | 'coaches'
  | 'mari-contacts';

function buildNotesPagination(
  page: number,
  pageSize: number,
  totalRecords: number,
): PaginationInfo {
  const totalPages = totalRecords === 0 ? 0 : Math.ceil(totalRecords / pageSize);

  return {
    page,
    pageSize,
    totalRecords,
    totalPages,
    hasNextPage: totalPages > 0 && page < totalPages,
    hasPreviousPage: page > 1 && totalPages > 0,
  };
}

class StudentConsolidatedHistoryServiceClass {
  async getByStudentId(
    studentId: number,
    options: {
      section: StudentHistorySection;
      notesPage?: number;
      notesPageSize?: number;
    },
  ): Promise<StudentConsolidatedHistoryResponse> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const url = new URL(`${config.supabaseFunctionsUrl}/get-student-history`);
    url.searchParams.set('studentId', String(studentId));
    url.searchParams.set('section', options.section);
    if (options.notesPage != null) {
      url.searchParams.set('notesPage', String(options.notesPage));
    }
    if (options.notesPageSize != null) {
      url.searchParams.set('notesPageSize', String(options.notesPageSize));
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error(err?.error || 'Erro ao carregar histórico da aluna'),
        'carregar histórico da aluna',
      );
    }

    const payload = await response.json() as StudentConsolidatedHistoryResponse;
    const notesPage = options.notesPage ?? 1;
    const notesPageSize = options.notesPageSize ?? 5;
    const notes = Array.isArray(payload.student_notes) ? payload.student_notes : [];
    const mariContacts = Array.isArray(payload.mari_contacts) ? payload.mari_contacts : [];
    const elapsedPayload = payload.elapsed_since_last_mari_contact;
    const elapsedSinceLastMariContact = elapsedPayload
      && typeof elapsedPayload.days === 'number'
      && typeof elapsedPayload.hours === 'number'
      && typeof elapsedPayload.minutes === 'number'
      && typeof elapsedPayload.seconds === 'number'
      ? {
        days: elapsedPayload.days,
        hours: elapsedPayload.hours,
        minutes: elapsedPayload.minutes,
        seconds: elapsedPayload.seconds,
      }
      : null;

    if (payload.notes_pagination) {
      return {
        ...payload,
        student_notes: notes,
        mari_contacts: mariContacts,
        elapsed_since_last_mari_contact: elapsedSinceLastMariContact,
      };
    }

    const from = (notesPage - 1) * notesPageSize;
    return {
      ...payload,
      student_notes: notes.slice(from, from + notesPageSize),
      notes_pagination: buildNotesPagination(notesPage, notesPageSize, notes.length),
      mari_contacts: mariContacts,
      elapsed_since_last_mari_contact: elapsedSinceLastMariContact,
    };
  }
}

export const StudentConsolidatedHistoryService = new StudentConsolidatedHistoryServiceClass();
